import React from "react";

import { Calendar, Check, Mail, User } from "lucide-react";

import { RelationshipAssessmentStakeholder } from "../../Store/Reducers/RelationshipSlice";
import { formatDateUSShort, getInitials } from "../../Utils/Helper/Helper";

export const formatStakeholderLabel = (label: string): string => {
  const labelMap: Record<string, string> = {
    executive_sponsor: "Executive Sponsor",
    account_team: "Account Team",
    c_level: "C-Level",
    executive: "Executive",
  };

  return labelMap[label] || label.replace(/_/g, " ");
};

const LABEL_BADGE_CLASS: Record<string, string> = {
  executive_sponsor: "bg-indigo-50 text-indigo-700",
  account_team: "bg-emerald-50 text-emerald-700",
  c_level: "bg-sky-50 text-sky-700",
  executive: "bg-slate-100 text-slate-700",
};

function labelBadgeClass(label: string): string {
  return LABEL_BADGE_CLASS[label] || "bg-slate-100 text-slate-600";
}

interface SponsorStakeholderCardProps {
  stakeholder: RelationshipAssessmentStakeholder;
  isSelected: boolean;
  onToggle: () => void;
  isCurrentUser?: boolean;
}

const SponsorStakeholderCard: React.FC<SponsorStakeholderCardProps> = ({
  stakeholder,
  isSelected,
  onToggle,
  isCurrentUser = false,
}) => {
  const displayName = stakeholder.user_name?.trim() || "Unknown";
  const subtitle =
    stakeholder.title ||
    stakeholder.role ||
    formatStakeholderLabel(stakeholder.label);

  return (
    <button
      type="button"
      onClick={onToggle}
      aria-pressed={isSelected}
      className={`group relative flex h-full min-h-[168px] w-full flex-col rounded-lg border p-3.5 text-left transition-shadow duration-150 focus:outline-none focus-visible:ring-2 focus-visible:ring-violet-200 focus-visible:ring-offset-1 ${
        isSelected
          ? "border-violet-200 bg-violet-50 shadow-[0_1px_3px_rgba(109,40,217,0.06)]"
          : "border-gray-200 bg-white hover:border-gray-300 hover:shadow-[0_2px_8px_rgba(15,23,42,0.06)]"
      }`}
    >
      <div
        className={`absolute right-2.5 top-2.5 flex h-[18px] w-[18px] items-center justify-center rounded-full transition-colors ${
          isSelected
            ? "bg-violet-400 text-white"
            : "border border-gray-300 bg-white group-hover:border-gray-400"
        }`}
        aria-hidden
      >
        {isSelected ? <Check className="h-2.5 w-2.5" strokeWidth={3} /> : null}
      </div>

      <div className="mb-3 flex items-start gap-2.5 pr-6">
        <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border border-slate-200 bg-slate-100 text-[11px] font-semibold text-slate-700">
          {displayName ? (
            getInitials(displayName)
          ) : (
            <User className="h-4 w-4 text-slate-500" />
          )}
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-1.5 flex-wrap">
            <h3
              className="truncate text-[13px] font-semibold leading-tight text-gray-900"
              title={displayName}
            >
              {displayName}
            </h3>
            {isCurrentUser ? (
              <span className="inline-flex items-center rounded px-1 py-0.5 text-[9px] font-semibold uppercase tracking-wide bg-violet-100 text-violet-700 shrink-0">
                You
              </span>
            ) : null}
          </div>
          <p
            className="mt-0.5 truncate text-[11px] leading-snug text-gray-500"
            title={subtitle}
          >
            {subtitle}
          </p>
        </div>
      </div>

      <div className="mb-3 space-y-1.5 border-t border-gray-100 pt-2.5">
        <div className="flex min-w-0 items-center gap-1.5 text-[11px] text-gray-600">
          <Mail className="h-3 w-3 shrink-0 text-gray-400" aria-hidden />
          <span className="truncate" title={stakeholder.email}>
            {stakeholder.email || "N/A"}
          </span>
        </div>
        <div className="flex min-w-0 items-center gap-1.5 text-[11px] text-gray-600">
          <Calendar className="h-3 w-3 shrink-0 text-gray-400" aria-hidden />
          <span className="truncate">
            Joined{" "}
            {stakeholder.join_date
              ? formatDateUSShort(stakeholder.join_date, true)
              : "N/A"}
          </span>
        </div>
      </div>

      <div className="mt-auto flex flex-wrap gap-1">
        {stakeholder.grade_name ? (
          <span className="inline-flex items-center rounded px-1.5 py-0.5 text-[10px] font-medium text-amber-800 bg-amber-50">
            {stakeholder.grade_name}
          </span>
        ) : null}
        <span
          className={`inline-flex items-center rounded px-1.5 py-0.5 text-[10px] font-medium ${labelBadgeClass(stakeholder.label)}`}
        >
          {formatStakeholderLabel(stakeholder.label)}
        </span>
      </div>
    </button>
  );
};

export default SponsorStakeholderCard;
